import React from 'react';

import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import Image from '~/components/Common/Image';
import DebugModal from '~/components/DebugModal/DebugModal';
import PropertyUploadData from '~/components/Vendors/Properties/PropertyUploadData';
import formatGeoData from '~/components/Vendors/helpers/formatGeoData';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import { formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';

import getPropertyTypeName from '~/utils/getPropertyTypeName';

import InfoCard from './InfoCard';
import LocationMapSection from './LocationMapSection';
import PropertyOverviewRow from './PropertyOverviewRow';
import SectionHeading from './SectionHeading';
import type { OfferBuilderProperty } from './types';

export type { OfferBuilderProperty } from './types';

interface PropertyInformationProps {
  property?: OfferBuilderProperty;
  vendorId?: string;
  isLoadingProperty: boolean;
  offerError: Error | null;
  propertyError: Error | null;
}

function formatValue(value?: string | number | null) {
  return value === null || value === undefined || value === '' ? '-' : value;
}

function formatEnabledByDefault(value?: boolean) {
  if (value == null) {
    return 'Yes';
  }

  return value ? 'Yes' : 'No';
}

export function formatPropertyLocation(property?: OfferBuilderProperty) {
  const city = property?.location_heading;
  const state = property?.geo_data?.administrative_area_level_1;
  const country = property?.geo_data?.country;
  const cityAndState = [city, state].filter(Boolean).join(', ');

  return [cityAndState, country].filter(Boolean).join(' · ') || '-';
}

export default function PropertyInformation({
  property,
  vendorId,
  isLoadingProperty,
  offerError,
  propertyError,
}: PropertyInformationProps) {
  const location = useLocation();
  const returnParams = new URLSearchParams(location.search);
  returnParams.set('contentTab', 'Property information');
  const returnTo = encodeURIComponent(`${location.pathname}?${returnParams.toString()}${location.hash}`);

  return (
    <Box>
      <SectionHeading
        title="Property information"
        subtitle="Verify the core property record. Coordinates must match the map pin and address."
        actions={
          <>
            <DebugModal type="property" title="Debug" data={property || {}} buttonProps={{ size: 'small' }} />
            {vendorId && property?.id ? (
              <Button
                component={Link}
                to={`/vendors/${vendorId}/edit-property/${property.id}?returnTo=${returnTo}`}
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
            ) : (
              <Button variant="contained" size="small" startIcon={<EditIcon />} disabled>
                Edit
              </Button>
            )}
          </>
        }
      />

      {isLoadingProperty && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading property data...
        </Alert>
      )}

      {offerError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load offer data: {offerError.message}
        </Alert>
      )}

      {propertyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load property data: {propertyError.message}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px', xl: 'minmax(0, 1fr) 320px' },
          gap: { xs: 2, xl: 3 },
          alignItems: 'start',
        }}
      >
        <Paper variant="outlined">
          <Box sx={{ p: 2.5 }}>
            <Typography variant="h6">Property overview</Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <PropertyOverviewRow label="Hotel code" value={property?.hotel_code || '-'} />
            <PropertyOverviewRow label="Address" value={property?.address || '-'} />
            <PropertyOverviewRow label="Type" value={getPropertyTypeName(property?.type)} />
            <PropertyOverviewRow label="Category" value={property?.category || '-'} />
            <PropertyOverviewRow label="Location city" value={property?.location_heading || '-'} />
            <PropertyOverviewRow label="Location country/state" value={property?.location_subheading || '-'} />
            <PropertyOverviewRow label="Children policy" value={property?.children_policy || '-'} />
            <PropertyOverviewRow label="Max child age" value={formatValue(property?.max_child_age)} />
            <PropertyOverviewRow label="Max infant age" value={formatValue(property?.max_infant_age)} />
            <PropertyOverviewRow
              label="Max teenager age"
              value={property?.max_teenager_age != null ? formatValue(property.max_teenager_age) : 'Not enabled'}
            />
            <PropertyOverviewRow label="Taxes and fees" value={property?.taxes_and_fees_value || '-'} />
            <PropertyOverviewRow label="Geolocation data" value={formatGeoData(property?.geo_data, 'property')} />
            {property?.taxes_payable_at_property && (
              <PropertyOverviewRow label="Has taxes and fees payable at property" value="Yes" />
            )}
            {property?.channel_manager === PROPERTY_CHANNEL_MANAGERS.derbysoft && (
              <>
                <PropertyOverviewRow
                  label="Send nightly reservation rates"
                  value={property.send_nightly_reservation_rates ? 'Yes' : 'No'}
                />
                <PropertyOverviewRow
                  label="Generate infant capacities from children"
                  value={property.generate_infant_capacities_from_children ? 'Yes' : 'No'}
                />
              </>
            )}
            {property?.exclude_flash_pay_at_property && (
              <PropertyOverviewRow label="Exclude Pay @ Property for LTLE" value="Yes" />
            )}
            {property && property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.selfManaged && (
              <PropertyOverviewRow
                label="LPC auto LuxPlus margin sharing enabled"
                value={formatEnabledByDefault(property.margin_sharing_enabled)}
              />
            )}
          </Box>
        </Paper>

        <Stack spacing={2}>
          {property?.channel_manager && (
            <InfoCard title="Channel manager">
              <Chip label={property.channel_manager} color="info" size="small" sx={{ alignSelf: 'flex-start' }} />
            </InfoCard>
          )}

          {property?.channel_managed && (
            <InfoCard title="Last ARI update">
              <Typography variant="body2">
                {property.ari_last_updated_at
                  ? formatDateLongMonthWithMeridiemNoSeconds(property.ari_last_updated_at)
                  : 'Never'}
              </Typography>
            </InfoCard>
          )}

          {property?.logo_id_cloudinary_external && (
            <InfoCard title="Logo">
              <Box
                sx={{
                  display: 'grid',
                  minHeight: 96,
                  placeItems: 'center',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'background.default',
                  '& img': {
                    maxHeight: 96,
                    objectFit: 'contain',
                  },
                }}
              >
                <Image className="img-responsive" publicId={property.logo_id_cloudinary_external} loading="lazy" />
              </Box>
            </InfoCard>
          )}

          <InfoCard title="Property meta">
            {property?.hotel_meta_provider ? (
              <Stack spacing={0.5}>
                <Typography variant="body2">Provider: {property.hotel_meta_provider}</Typography>
                <Typography variant="body2">Code: {property.hotel_meta_code || 'Not provided'}</Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Property not mapped to any meta provider.
              </Typography>
            )}
          </InfoCard>

          <InfoCard title="Property data">
            <PropertyUploadData
              vendorId={vendorId || ''}
              propertyId={property?.id}
              channelManager={property?.channel_manager}
              hideTitle
              buttonProps={{
                variant: 'outlined',
                disabled: !vendorId || !property?.id,
                fullWidth: true,
                startIcon: <UploadFileIcon />,
              }}
            />
          </InfoCard>
        </Stack>
      </Box>

      <LocationMapSection property={property} />
    </Box>
  );
}
